import { defineAsyncComponent } from 'vue';
import { TabItem } from '@/types/common';
import { PermissionsAirMblFunctions } from '@/constants/permissions';
import { MblContainer } from '@/types/air/mbl/container.entity';
import { GeneralVolumeUnits, GeneralWeightUnits } from '@/constants/measurementUnits';

export const MAWB_VIEW_TABS = {
  mblDetails: 'MBL_DETAILS',
  containers: 'CONTAINERS',
  hblList: 'HBL_LIST',
  milestones: 'MILESTONES',
  charges: 'CHARGES',
  attachments: 'ATTACHMENTS',
  pttApplication: 'PTT_APPLICATION',
  print: 'PRINT',
};

export const MAWB_VIEW_TABS_OPTIONS: TabItem[] = [
  { text: 'MawbDetails', value: MAWB_VIEW_TABS.mblDetails },
  {
    text: 'Containers',
    value: MAWB_VIEW_TABS.containers,
    permissionKey: PermissionsAirMblFunctions.HBL_LIST,
  },
  {
    text: 'HblList',
    value: MAWB_VIEW_TABS.hblList,
    permissionKey: PermissionsAirMblFunctions.HBL_LIST,
  },
  {
    text: 'Milestones',
    value: MAWB_VIEW_TABS.milestones,
    permissionKey: PermissionsAirMblFunctions.MILESTONES,
  },
  {
    text: 'Charges',
    value: MAWB_VIEW_TABS.charges,
    permissionKey: [
      PermissionsAirMblFunctions.AUTO_CHARGES,
      PermissionsAirMblFunctions.MANUAL_CHARGES,
    ],
  },
  {
    text: 'Attachments',
    value: MAWB_VIEW_TABS.attachments,
    permissionKey: PermissionsAirMblFunctions.ATTACHMENTS,
  },
  {
    text: 'PTT Application',
    value: MAWB_VIEW_TABS.pttApplication,
    permissionKey: PermissionsAirMblFunctions.PTT,
  },
  { text: 'Print', value: MAWB_VIEW_TABS.print, permissionKey: PermissionsAirMblFunctions.PRINT },
];

export const MAWB_FORM_STEPS = [
  {
    label: 'PolPodDetails',
    isValid: true,
    component: defineAsyncComponent(
      () => import('@/components/air-imports/mawb/forms/components/PolPodDetailsFormComponent.vue')
    ),
  },
  {
    label: 'ContainerDetails',
    isValid: true,
    component: defineAsyncComponent(
      () =>
        import('@/components/air-imports/mawb/forms/components/ContainerDetailsFormComponent.vue')
    ),
  },
  {
    label: 'DischargeDetails',
    isValid: true,
    component: defineAsyncComponent(
      () => import('@/components/air-imports/mawb/forms/components/DischargeDetails.vue')
    ),
  },
  {
    label: 'CFSReceipt',
    isValid: true,
    component: defineAsyncComponent(
      () => import('@/components/air-imports/mawb/forms/components/CFSReceiptFormComponent.vue')
    ),
  },
  {
    label: 'Notes',
    isValid: true,
    component: defineAsyncComponent(
      () => import('@/components/air-imports/mawb/forms/components/NotesFormComponent.vue')
    ),
  },
];

export const MAWB_FORM_DEFAULT_DATA = () => {
  return {
    pod: null,
    warehouse: null,
    service: null,
    customerReference: '',
    customerContactName: '',
    customerContact: '',
    airportGroundHandler: null,
    paymentLookup: null,
    division: null,
    customer: null,
    mblNumber: '',
    carrier: null,
    flightNumber: '',
    freightForwarder: '',
    mawb: '',
    mib: '',
    containers: [MAWB_DEFAULT_CONTAINER_DATA()],
    vessel: {
      vessel_name: '',
      imo: '',
      mmsi: '',
    },
    voyage: '',
    pol: null,
    departureDate: null,
    sailDate: null,
    eta: '',
    containerType: null,
    containerNumber: '',
    sealNumber: '',
    noOfPcs: 0,
    packageType: null,
    measure: 0,
    weight: 0,
    weightUnit: 'KGS',
    measureUnit: 'CBM',
    pttRequiredDate: null,
    pttApprovalDate: null,
    hazmat: false,
    headLoad: false,
    dischargeDate: null,
    clearedCbpExamDate: null,
    devanStartTime: null,
    devanEndTime: null,
    devanHours: 0,
    terminal: null,
    appointmentDate: null,
    lastFreeDate: null,
    pickupCarrier: null,
    terminalDispatchDate: null,
    destinationWarehouse: null,
    deliveryNotes: '',
    cfsReceivedDate: null,
    cfsLocation: '',
    moveToDoorDate: null,
    inventoryLocation: '',
    door: '',
    tir: '',
    tirDate: null,
    chassis: '',
    stripDate: null,
    strippedBy: '',
    devanLabourCost: '',
    labour: [],
    headLoadDate: null,
    headLoadFreeDate: null,
    emptyReturnDate: null,
    emptyReturnLocation: null,
    documentReceivedDate: new Date(),
    terminalAppointmentDate: null,
    otherEmptyReturnLocation: '',
    goDate: null,
    internalNotes: '',
    externalNotes: '',
    dispatchNotes: '',
    containerPics: [],
    cargoDescription: 'FAK',
    loadType: 'Mixed',
    chassisRentalDays: '',
    warehouseLoc: null,
    warehouseLfd: null,
    overrideLfd: false,
    availableDate: null,
    isPttRequired: true,

    etaTimeZone: '',
    goDateTimeZone: '',
    tirDateTimeZone: '',
    sailDateTimeZone: '',
    closeDateTimeZone: '',
    stripDateTimeZone: '',
    devanEndTimeTimeZone: '',
    warehouseLfdTimeZone: '',
    lastFreeDateTimeZone: '',
    headLoadDateTimeZone: '',
    dischargeDateTimeZone: '',
    availableDateTimeZone: '',
    devanStartTimeTimeZone: '',
    moveToDoorDateTimeZone: '',
    appointmentDateTimeZone: '',
    emptyReturnDateTimeZone: '',
    pttRequiredDateTimeZone: '',
    cfsReceivedDateTimeZone: '',
    pttApprovalDateTimeZone: '',
    headLoadFreeDateTimeZone: '',
    clearedCbpExamDateTimeZone: '',
    documentReceivedDateTimeZone: '',
    terminalDispatchDateTimeZone: '',
    terminalAppointmentDateTimeZone: '',
  };
};

export const MAWB_DEFAULT_CONTAINER_DATA = (): Omit<MblContainer, 'isActive' | '_id'> => ({
  containerName: '',
  containerNumber: '',
  sealNumber: '',
  noOfPcs: 0,
  weight: 0,
  measure: 0,
  weightUnit: GeneralWeightUnits.KGS,
  measureUnit: GeneralVolumeUnits.CBM,
  containerType: null,
  containerNumberCheck: false,
  packageType: 'PACKAGE',
});

export const MAWB_SERVICE = {
  STANDARD: 'standard',
  GENERAL_CARGO: 'general_cargo',
  PARCEL_TYPE: 'parcel_type',
  SMALL_PACKAGE: 'small_package',
} as const;

export const MAWB_SERVICE_OPTIONS = [
  {
    description: 'Standard',
    code: 'standard',
  },
  {
    description: 'General Cargo',
    code: 'general_cargo',
  },
  {
    description: 'Parcel Type',
    code: 'parcel_type',
  },
  {
    description: 'Small Package',
    code: 'small_package',
  },
] as const;
